import { useCallback, useEffect, useState } from 'react'
import { useLocationStore } from '@/store/location'
import { getCurrentPosition, DEFAULT_LOCATION } from '@/lib/geo/location'
import { resolveLocation } from '@/lib/nws/client'

/**
 * Resolves the active location for the Home view:
 *  - reuse the saved location if present,
 *  - otherwise try device geolocation once (auto), resolving the city/state via NWS,
 *  - exposing manual `locate()` and `useDefault()` for the location prompt.
 */
export function useActiveLocation() {
  // Narrow selectors so Home only re-renders on `saved`/`autoLocateTried` — not on
  // saved-list / home-marker churn from the Locations view (actions are stable refs).
  const saved = useLocationStore((s) => s.saved)
  const setSaved = useLocationStore((s) => s.setSaved)
  const autoLocateTried = useLocationStore((s) => s.autoLocateTried)
  const setAutoLocateTried = useLocationStore((s) => s.setAutoLocateTried)
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const locate = useCallback(async () => {
    setLocating(true)
    setError(null)
    try {
      const { lat, lon } = await getCurrentPosition()
      const resolved = await resolveLocation(lat, lon)
      setSaved({ lat: resolved.lat, lon: resolved.lon, label: resolved.label })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not locate you.')
    } finally {
      setLocating(false)
    }
  }, [setSaved])

  const useDefault = useCallback(() => setSaved(DEFAULT_LOCATION), [setSaved])

  // Auto-locate once on first ever launch.
  useEffect(() => {
    if (!saved && !autoLocateTried) {
      setAutoLocateTried(true)
      void locate()
    }
  }, [saved, autoLocateTried, setAutoLocateTried, locate])

  return { saved, locating, error, locate, useDefault, setSaved }
}
